// @ts-nocheck
import React, {useEffect, useState} from 'react';
import {
  ActivityIndicator,
  Linking,
  StyleSheet,
  useWindowDimensions,
  View,
  Text,
} from 'react-native';
import {TouchableRipple} from 'react-native-paper';
import {useDispatch} from 'react-redux';
import {Alert} from '../types';
import {EventNames} from '../../../@types/EventNames';
import {residentApiWithAuth} from '../../../api/resident';
import Avatar from '../../../components/Avatar';
import useColorMode from '../../../components/useColorMode';
import {VISITOR_SCREEN_TIMEOUT_DURATION} from '../../../constants';
import isWeb from '../../../utils/isWeb';
import ResponseBadge from './ReponseBadge';
import {HandleAlertButton} from '../alertsReducer';
import Feather from '@expo/vector-icons/Feather';
import Ionicons from '@expo/vector-icons/Ionicons';

const NotificationsItem = props => {
  const notification: Alert = props.notification;
  const {clickHandler} = props;
  const {colorMode} = useColorMode();
  const dispatch = useDispatch();
  const message = notification.message;
  const insUnread = notification.read !== true;
  const [alertTimedOut, setAlertTimedOut] = useState(false);
  const [buttonsShown, setButtonsShown] = useState(false);
  const [image, setImage] = useState(null);
  const [loadingImage, setImageLoading] = useState(true);
  const {width} = useWindowDimensions();

  const isDark = colorMode === 'dark';
  const rowBackground = isDark ? '#18181b' : '#ffffff';
  const messageTextColor = isDark
    ? insUnread
      ? '#f4f4f5'
      : '#a1a1aa'
    : insUnread
    ? '#18181b'
    : '#71717a';
  const mutedTextColor = isDark ? '#a1a1aa' : '#71717a';
  const buttonContainerWidth = Math.max(width - 96, 220);

  let action = notification.action;

  useEffect(() => {
    const isVisitorScreen =
      notification.eventName === EventNames.VISITOR_SCREEN;
    const repliedTo = !!action;
    const receivedInLast15Mins = 
      new Date().getTime() - new Date(notification.time).getTime() >
      VISITOR_SCREEN_TIMEOUT_DURATION;

    if (isVisitorScreen) {
      if (receivedInLast15Mins && !repliedTo) {
        setAlertTimedOut(true);
        setButtonsShown(false);
      } else {
        setAlertTimedOut(false);
        setButtonsShown(true);
      }
    }
  }, [notification]);

  useEffect(() => {
    fetchImage(notification.id);
  }, [notification.id]);

  async function fetchImage(notificationId) {
    try {
      const res = await residentApiWithAuth().get(
        `/alertDetail/person/image/${notificationId}`,
      );
      const data = await res.data.data;
      setImage(data.image);
    } catch (error) {
    } finally {
      setImageLoading(false);
    }
  }

  return (
    <TouchableRipple
      onPress={() => clickHandler()}
      style={[
        styles.card,
        {backgroundColor: rowBackground},
        insUnread && styles.unreadCard,
        notification.alertBackgroundColor !== null && {
          backgroundColor: notification.alertBackgroundColor,
        },
      ]}>
      <View style={styles.container}>
        <View style={styles.avatarColumn}>
          {loadingImage ? (
            <Avatar size="sm">
              <ActivityIndicator size="small" />
            </Avatar>
          ) : (
            <>
              {image ? (
                <Avatar
                  size="sm"
                  source={{
                    uri: `data:image/jpeg;base64,${image}`,
                  }}>
                  {notification.eventName?.charAt(0) ?? ''}
                </Avatar>
              ) : (
                <Avatar size="sm">{notification.eventName?.charAt(0) ?? ''}</Avatar>
              )}
            </>
          )}
        </View>
        <View style={styles.content}>
          <View style={styles.headerRow}>
            <View style={styles.badgeRow}>
              <View style={styles.eventBadge}>
                <Ionicons name="notifications-outline" size={11} color="#6366f1" />
                <Text numberOfLines={1} style={styles.eventText}>
                  {notification.eventName}
                </Text>
              </View>
              {!!action && <ResponseBadge action={action} />}
              {alertTimedOut && (
                <View style={styles.timedOutContainer}>
                  <Text style={styles.timedOutText}>Timed out</Text>
                </View>
              )}
            </View>
            <Text numberOfLines={1} style={[styles.timeText, {color: mutedTextColor}]}>
              {new Date(notification.time).toLocaleString()}
            </Text>
          </View>
          <Text
            numberOfLines={3}
            style={[styles.messageText, {color: messageTextColor}]}>
            {message}
          </Text>
          {buttonsShown && !alertTimedOut && (
            <View
              style={[
                styles.buttonContainer,
                {
                  width: buttonContainerWidth,
                },
              ]}>
              {notification.button1 && (
                <TouchableRipple
                  onPress={() => {
                    setButtonsShown(false);
                    dispatch(
                      HandleAlertButton(
                        notification,
                        notification.button1.text,
                        parseInt(notification.button1.value, 10),
                      ),
                    );
                  }}>
                  <View style={styles.button1}>
                    <Text style={styles.buttonText}>
                      {notification.button1.text}
                    </Text>
                  </View>
                </TouchableRipple>
              )}
              {notification.button2 && (
                <TouchableRipple
                  onPress={() => {
                    setButtonsShown(false);
                    dispatch(
                      HandleAlertButton(
                        notification,
                        notification.button2.text,
                        parseInt(notification.button2.value, 10),
                      ),
                    );
                  }}>
                  <View style={styles.button2}>
                    <Text style={styles.buttonText}>
                      {notification.button2.text}
                    </Text>
                  </View>
                </TouchableRipple>
              )}
              {notification.button3 &&
                notification.phoneNumber !== '' &&
                notification.phoneNumber !== null &&
                notification.button3.text === 'Call' &&
                (
                  <TouchableRipple
                    onPress={() =>
                      Linking.openURL(`tel:${notification.phoneNumber}`)
                    }>
                    <View style={styles.button3}>
                      <Text style={styles.buttonText}>
                        {notification.button3.text}
                      </Text>
                    </View>
                  </TouchableRipple>
                )}
            </View>
          )}
        </View>
        <View style={styles.chevronColumn}>
          <Feather name="chevron-right" size={20} color={mutedTextColor} />
        </View>
      </View>
    </TouchableRipple>
  );
};

const styles = StyleSheet.create({
  card: {
    borderColor: '#e4e4e7',
    borderRadius: 8,
    borderWidth: 1,
    marginHorizontal: 12,
    marginVertical: 5,
    overflow: 'hidden',
  },
  unreadCard: {
    borderLeftColor: '#039be5',
    borderLeftWidth: 4,
  },
  container: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: 10,
    paddingVertical: 10,
  },
  avatarColumn: {
    alignSelf: 'center',
  },
  content: {
    flex: 1,
    marginLeft: 10,
    minWidth: 0,
  },
  headerRow: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 8,
    marginBottom: 4,
  },
  badgeRow: {
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    flex: 1,
    gap: 6,
  },
  timedOutContainer: {
    backgroundColor: '#fef3c7',
    borderRadius: 5,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  timedOutText: {
    color: '#92400e',
    fontSize: 11,
    fontWeight: '700',
  },
  unreadPill: {
    backgroundColor: '#dbeafe',
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  unreadPillText: {
    color: '#1d4ed8',
    fontSize: 11,
    fontWeight: '700',
  },
  timeText: {
    flexShrink: 0,
    fontSize: 10,
    lineHeight: 16,
    maxWidth: isWeb ? 220 : 132,
    textAlign: 'right',
  },
  messageText: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 19,
  },
  eventBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: '#eef2ff',
    borderRadius: 5,
    paddingHorizontal: 8,
    paddingVertical: 3,
    alignSelf: 'flex-start',
    flexShrink: 1,
  },
  eventText: {
    color: '#6366f1',
    fontSize: 10,
    fontWeight: '700',
    textTransform: 'uppercase',
    flexShrink: 1,
  },
  buttonContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'center',
    marginTop: 10,
  },
  button1: {
    flexDirection: 'row',
    paddingHorizontal: 12,
    paddingVertical: 7,
    backgroundColor: '#16a34a',
    borderRadius: 4,
  },
  button2: {
    flexDirection: 'row',
    paddingHorizontal: 12,
    paddingVertical: 7,
    backgroundColor: '#dc2626',
    borderRadius: 4,
  },
  button3: {
    flexDirection: 'row',
    paddingHorizontal: 12,
    paddingVertical: 7,
    backgroundColor: '#2563eb',
    borderRadius: 4,
  },
  buttonText: {
    color: '#fff',
    textAlign: 'center',
    fontSize: 12,
    fontWeight: '700',
  },
  chevronColumn: {
    alignSelf: 'center',
    marginLeft: 6,
    marginRight: 2,
  },
});

export default NotificationsItem;
